import { rpx, RpxResponse } from 'client/lib/rpx-client';
import { defCoursesRoute } from '@course/components/courses-app-router';
import { LoadedProps, RouteLoadProps, useRouter } from '@components/router';
import { useState } from 'preact/hooks';
import { useMinidoc } from '@components/minidoc';
import { cardMiddleware, defaultToolbarActions, minidocToolbar, placeholder } from 'minidoc-editor';
import { mediaCard, mediaMiddleware } from '@components/media-card';
import { toolbarDropdown } from '@components/toolbar-dropdown';
import { useIntl } from 'shared/intl/use-intl';
import { ManualDom } from '@components/manual-dom';
import { BtnPrimary, BtnSecondary } from '@components/buttons';
import { Toggle } from '@components/toggle';
import { IcoTrash, IcoPlus, IcoExternalLink } from '@components/icons';
import { showDialog } from '@components/dialog';
import { showError } from '@components/app-error';
import { Spinner } from '@components/spinner';

type PageList = RpxResponse<typeof rpx.customPages.listPages>;
type PageItem = PageList[0];

async function load(route: RouteLoadProps) {
  const pages = await rpx.customPages.listPages();
  const editId = route.params.id;

  let editPage: RpxResponse<typeof rpx.customPages.getPageById> | undefined;
  if (editId) {
    editPage = await rpx.customPages.getPageById({ id: editId }).catch(() => undefined);
  }

  return { pages, editId, editPage };
}

type Props = LoadedProps<typeof load>;

export const route = defCoursesRoute({
  authLevel: 'guide',
  load,
  Page,
});

function PageListView({ pages }: { pages: PageItem[] }) {
  const router = useRouter();
  const [isCreating, setIsCreating] = useState(false);
  const [newSlug, setNewSlug] = useState('');
  const [newTitle, setNewTitle] = useState('');

  async function handleCreate() {
    if (!newSlug || !newTitle) {
      return;
    }
    setIsCreating(true);
    try {
      const page = await rpx.customPages.createPage({
        slug: newSlug,
        title: newTitle,
      });
      router.goto(`/admin/pages/${page.id}`);
    } catch (err) {
      showError(err as Error);
    } finally {
      setIsCreating(false);
    }
  }

  return (
    <div class="max-w-4xl mx-auto p-6">
      <div class="flex items-center justify-between mb-6">
        <h1 class="text-2xl font-bold">Custom Pages</h1>
      </div>

      <div class="bg-white rounded-lg border mb-6 p-4">
        <p class="text-sm text-gray-500 mb-3">Create a new page</p>
        <div class="flex gap-2 items-center">
          <input
            class="ruz-input text-sm"
            placeholder="slug (e.g. about)"
            value={newSlug}
            onInput={(e: any) =>
              setNewSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))
            }
          />
          <input
            class="ruz-input text-sm"
            placeholder="Page title"
            value={newTitle}
            onInput={(e: any) => setNewTitle(e.target.value)}
          />
          <BtnPrimary
            class="gap-1 text-sm whitespace-nowrap"
            disabled={isCreating || !newSlug || !newTitle}
            onClick={handleCreate}
          >
            <IcoPlus />
            Create
          </BtnPrimary>
        </div>
      </div>

      {pages.length === 0 && <p class="text-gray-500 p-8 text-center">No custom pages yet.</p>}

      <div class="bg-white rounded-lg border">
        {pages.map((page) => (
          <a
            key={page.id}
            href={`/admin/pages/${page.id}`}
            class="flex items-center gap-4 p-4 border-b last:border-b-0 hover:bg-gray-50"
          >
            <div class="flex flex-col gap-1 grow">
              <span class="font-medium">{page.title}</span>
              <span class="text-sm text-gray-500">/p/{page.slug}</span>
            </div>
            <span
              class={`text-xs px-2 py-0.5 rounded-full ${
                page.published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
              }`}
            >
              {page.published ? 'Published' : 'Draft'}
            </span>
          </a>
        ))}
      </div>
    </div>
  );
}

function PageEditor({
  page,
  pageId,
  pages,
}: {
  page?: { title: string; body: string; slug: string };
  pageId: string;
  pages: PageItem[];
}) {
  const router = useRouter();
  const intl = useIntl();
  const pageInfo = pages.find((p) => p.id === pageId);

  const [title, setTitle] = useState(page?.title || pageInfo?.title || '');
  const [slug, setSlug] = useState(page?.slug || pageInfo?.slug || '');
  const [published, setPublished] = useState(pageInfo?.published || false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const bodyRef = { current: page?.body || '' };

  const editor = useMinidoc({
    doc: bodyRef.current,
    middleware: () => [
      placeholder('Page content...'),
      minidocToolbar([...defaultToolbarActions, toolbarDropdown({ intl, compact: true })]),
      cardMiddleware([mediaCard]),
      mediaMiddleware(),
    ],
    onChange: (content) => {
      bodyRef.current = content;
    },
  });

  async function handleSave() {
    setIsSaving(true);
    try {
      await rpx.customPages.updatePage({
        id: pageId,
        title,
        slug,
        body: bodyRef.current,
        published,
      });
    } catch (err) {
      showError(err as Error);
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete() {
    const confirmed = await showDialog({
      mode: 'warn',
      title: 'Delete page?',
      children: 'This will permanently delete this page.',
      confirmButtonText: 'Delete',
    });
    if (!confirmed) {
      return;
    }
    setIsDeleting(true);
    try {
      await rpx.customPages.deletePage({ id: pageId });
      router.goto('/admin/pages');
    } catch (err) {
      showError(err as Error);
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <div class="max-w-4xl mx-auto p-6">
      <div class="flex items-center gap-4 mb-6">
        <a href="/admin/pages" class="text-gray-500 hover:text-gray-700 text-sm">
          &larr; All pages
        </a>
        <a
          href={`/p/${slug}`}
          target="_blank"
          class="ml-auto text-sm text-indigo-600 flex items-center gap-1"
          rel="noreferrer"
        >
          <IcoExternalLink class="size-3.5" />
          View page
        </a>
      </div>

      <div class="bg-white rounded-lg border p-6 flex flex-col gap-6">
        <div class="flex gap-4">
          <div class="flex flex-col gap-1 grow">
            <label class="text-sm font-medium">Title</label>
            <input class="ruz-input" value={title} onInput={(e: any) => setTitle(e.target.value)} />
          </div>
          <div class="flex flex-col gap-1 w-48">
            <label class="text-sm font-medium">Slug</label>
            <input
              class="ruz-input"
              value={slug}
              onInput={(e: any) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            />
          </div>
        </div>

        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium">Content</label>
          <div class="border rounded-md ring-2 ring-transparent focus-within:ring-indigo-400">
            <header class="border-b flex">
              <ManualDom el={editor.toolbar.root} />
            </header>
            <ManualDom class="grow mini-padded *:min-h-60 *:p-3.5" el={editor.root} />
          </div>
        </div>

        <div class="flex items-center gap-2">
          <Toggle checked={published} onClick={() => setPublished(!published)} />
          <span class="text-sm">{published ? 'Published' : 'Draft'}</span>
        </div>

        <div class="flex items-center gap-3 pt-4 border-t">
          <BtnPrimary onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Spinner class="border-white" /> : 'Save'}
          </BtnPrimary>
          <BtnSecondary
            onClick={handleDelete}
            disabled={isDeleting}
            class="gap-1 text-red-600 hover:text-red-700"
          >
            <IcoTrash class="size-4" />
            Delete
          </BtnSecondary>
        </div>
      </div>
    </div>
  );
}

function Page(props: Props) {
  const { pages, editId, editPage } = props.state;

  if (editId) {
    return <PageEditor page={editPage} pageId={editId} pages={pages} />;
  }

  return <PageListView pages={pages} />;
}
